export type F2FDeliveryAudience = 'candidate' | 'attendee' | 'forwarded';

export type F2FDeliveryResult = {
  recipient: string;
  audience: F2FDeliveryAudience;
  success: boolean;
  status: string;
  error: string | null;
};

export type F2FDeliverySummary = {
  complete: boolean;
  sentCount: number;
  failedCount: number;
  deliveries: F2FDeliveryResult[];
};

export type F2FReschedulePayload = {
  success?: boolean;
  partial_success?: boolean;
  schedule_persisted?: boolean;
  notification_status?: string;
  notification_outcome_recorded?: boolean;
  delivery_summary?: F2FDeliverySummary;
  error?: string;
};

export type F2FRescheduleUiOutcome = 'notified' | 'saved_notification_failed' | 'failed';

function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson((value as Record<string, unknown>)[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
}

async function sha256Hex(value: string) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export async function f2fForwardEmailIdempotencyKey(args: {
  applicationId: string;
  recipient: string;
  details: unknown;
}) {
  const digest = await sha256Hex(stableJson({
    recipient: args.recipient.trim().toLowerCase(),
    details: args.details,
  }));
  return `f2f-forward-email:${args.applicationId}:${digest}`;
}

export function summarizeF2FDeliveryResults(deliveries: F2FDeliveryResult[]): F2FDeliverySummary {
  const sentCount = deliveries.filter((delivery) => delivery.success).length;
  const failedCount = deliveries.length - sentCount;
  return {
    complete: deliveries.length > 0 && failedCount === 0,
    sentCount,
    failedCount,
    deliveries,
  };
}

export function failedF2FDeliveryRecipients(summary: F2FDeliverySummary) {
  return summary.deliveries
    .filter((delivery) => !delivery.success)
    .map((delivery) => delivery.recipient);
}

export function resolveF2FRescheduleUiOutcome(
  payload: F2FReschedulePayload | null | undefined,
): F2FRescheduleUiOutcome {
  if (
    payload?.success === true &&
    payload.notification_outcome_recorded === true &&
    payload.delivery_summary?.complete === true
  ) {
    return 'notified';
  }
  if (payload?.schedule_persisted === true || payload?.partial_success === true) {
    return 'saved_notification_failed';
  }
  return 'failed';
}

export async function readF2FDeliveryErrorPayload(
  error: unknown,
): Promise<F2FReschedulePayload | null> {
  if (!error || typeof error !== 'object' || !('context' in error)) return null;
  const context = (error as { context?: { json?: () => Promise<unknown> } }).context;
  if (!context || typeof context.json !== 'function') return null;
  try {
    return await context.json() as F2FReschedulePayload;
  } catch {
    return null;
  }
}

export async function deliveryResultFromInvoke(
  recipient: string,
  result: { data: unknown; error: unknown },
): Promise<F2FDeliveryResult> {
  const errorPayload = result.error ? await readF2FDeliveryErrorPayload(result.error) : null;
  const payload = (errorPayload || result.data || {}) as Record<string, unknown>;
  const success = !result.error && payload.success === true;
  const sdkMessage = result.error && typeof result.error === 'object' && 'message' in result.error
    ? String((result.error as { message?: unknown }).message || '')
    : '';
  return {
    recipient,
    audience: 'forwarded',
    success,
    status: typeof payload.status === 'string' ? payload.status : success ? 'sent' : 'failed',
    error: success
      ? null
      : typeof payload.error === 'string'
        ? payload.error
        : sdkMessage || 'Email delivery failed',
  };
}
